import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { styles } from "@insite/content-library/Widgets/ContactUs/AvailableTopics";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import X from "@insite/mobius/Icons/X";
import Img from "@insite/mobius/Img/Img";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser"
import * as React from "react";
import { useState } from "react";
import styled, { css } from "styled-components";

type Props = OwnProps & HasProduct;
// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    descriptionFieldValue = "descriptionFieldValue",
    image = "image",
    imageAltText = "imageAltText",
    learnMoreLink = "learnMoreLink",
    linkName = "linkName",
    showLink = "showLink",
    linktype = "linktype",
    messageColor = "messageColor",
    iconTooltipText = "iconTooltipText",
    showIconTooltip = "showIconTooltip",
    badgeName = "badgeName",
}

interface OwnProps extends WidgetProps  {
    fields: {
        [fields.descriptionFieldValue]: string;
        [fields.image]: string;
        [fields.imageAltText]: string;
        [fields.learnMoreLink]: LinkFieldValue;
        [fields.linkName]: string;
        [fields.showLink]: boolean;
        [fields.linktype]:string;
        [fields.messageColor]: string;
        [fields.iconTooltipText]: string;
        [fields.showIconTooltip]: boolean;
        [fields.badgeName]:string;
    };
}

export interface DsgCommonMessageWidgetStyles {
    tooltipWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
}

export const dsgCommonMessageWidgetStyles: DsgCommonMessageWidgetStyles = {
    imageWrapper: {
        css: css`
            position: relative;
            display: inline-flex;
            img {
                margin-right: 16px;
                max-width: 24px;
                min-width: 24px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-right: 8px;
                    `,
                ],
                "min",
                )}
            }
            .tooltip-wrapper {
                position: absolute;
                display: flex;
                justify-content: space-between;
                left: 0;
                top: 24px;
                background: ${colorPallete.grey700};
                box-shadow: ${elevation.dp08};
                border-radius: 4px;
                padding: 16px;
                width: 275px;
                z-index: 1;
                color: ${colorPallete.white};
                font: ${labelFonts.label2Regular.fontWeight} ${labelFonts.label2Regular.fontSize}/${labelFonts.label2Regular.lineHeight} ${labelFonts.label2Regular.fontFamily};
                column-gap: 8px;
                .close-button {
                    height: 16px;
                }
                svg {
                    width: 16px;
                    height: 16px;
                }
            }
        `
    }
}

const stylesCustom = dsgCommonMessageWidgetStyles;

const Wrapper = styled.div`
    border-radius: 8px;
    border: 1px solid ${colorPallete.grey300};
    padding: 8px 16px;
    margin-bottom: 16px;
    &.hazardous-product {
        > div {
            flex-wrap: wrap;
        }
    }
    ${({ theme }: { theme: BaseTheme }) =>
    breakpointMediaQueries(
    theme,
    [
        null,
        null,
        css`
            padding: 16px 24px;
            &.hazardous-product {
                > div {
                    flex-wrap: initial;
                }
            }
        `,
    ],
    "min",
    )}
                    
`;

const ContentBlock = styled.div`
    display: flex;
    align-items: center;
    font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
    strong {
        font-weight: 600;
    }
    p:last-child:first-child {
        width: 100%;
    }
    ${({ theme }: { theme: BaseTheme }) =>
    breakpointMediaQueries(
    theme,
    [
        null,
        null,
        css`
            font-size: ${bodyFonts.body2RegularLg.fontSize};
            line-height: ${bodyFonts.body2RegularLg.lineHeight};
        `,
    ],
    "min",
    )}
`;


const Description = styled.p`
    width: calc(100% - 40px);
    ${({ theme }: { theme: BaseTheme }) =>
    breakpointMediaQueries(
    theme,
    [
        null,
        null,
        css`
            width: inherit;
        `,
    ],
    "min",
    )}
`;

const Link = styled.a`
    font: ${labelFonts.label2SemiBold.fontWeight} ${labelFonts.label2SemiBold.fontSize}/${labelFonts.label2SemiBold.lineHeight} ${labelFonts.label2SemiBold.fontFamily};
    text-decoration: underline;
    color: ${colorPallete.primaryRed500};
    margin-left: auto;
    padding-left: 16px;
    ${({ theme }: { theme: BaseTheme }) =>
    breakpointMediaQueries(
    theme,
    [
        null,
        null,
        css`
            padding-left: 8px;
        `,
    ],
    "min",
    )}
`;


 const DsgCommonMessageWidget: React.FC<Props> = props => {
    
    const { url, title } = useGetLink(props.fields.learnMoreLink);
    const [isVisibleTooltip, setIsVisibleTooltip] = useState(false);
    const { width } = useWindowSize();
    const handleMouseEnterTooltip = () => {
        setIsVisibleTooltip(true);
    };
    
    const handleMouseLeaveTooltip = () => {
        setIsVisibleTooltip(false);
    };
    
    const handleCloseClickTooltip = () => {
        setIsVisibleTooltip(false);
    };
    const scrollToTarget = () => {
       
        const specificationsAccordion = document.querySelector('[data-test-selector="productDetails_specificationAccordion"] .downloadFilesSection');
        const specificationsAccordionTargerEle: HTMLElement = document.querySelector('[data-test-selector="productDetails_specificationAccordion"] dd') as HTMLElement;
        const specificationsAccordionButton: HTMLElement = document.getElementsByClassName('button-Specifications')[0] as HTMLElement;
        const stickyHeader: HTMLElement = document.querySelector('.pdpStickyHeader') as HTMLElement;
        let headerHeight = 0;
        if (stickyHeader) {
            headerHeight = stickyHeader.offsetHeight;
        }
        if (specificationsAccordion) {
            if(stickyHeader && width > 1023) {
                const targetTop = specificationsAccordionButton.getBoundingClientRect().top + window.pageYOffset;
                const scrollPosition = targetTop - 90;
                window.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth'
                });
            }
            else {
                specificationsAccordion.scrollIntoView({ behavior: 'smooth' });
            }

            if((specificationsAccordionTargerEle?.hasAttribute('hidden'))) {
                if(specificationsAccordionButton) {
                    specificationsAccordionButton.click();
                } 
            }
        }
    };
    if(props.fields.linktype === "discontinued" && props.product.isDiscontinued) {
         return (
        <Wrapper className="discontinued-product">
            <ContentBlock>
                {props.fields.image && (
                    <StyledWrapper className="msg-image-wrapper" {...stylesCustom.imageWrapper} onMouseEnter={handleMouseEnterTooltip} onMouseLeave={handleMouseLeaveTooltip}>
                        <Img src={props.fields.image} altText={props.fields.imageAltText || ''} />
                        {props.fields.showIconTooltip && (
                            <>
                                {isVisibleTooltip && (
                                    <StyledWrapper className="tooltip-wrapper" {...stylesCustom.tooltipWrapper}>
                                        {props.fields.iconTooltipText}
                                        <Typography className="close-button" onClick={handleCloseClickTooltip}>
                                            <X/>
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                )}
                <Description style={{color: props.fields.messageColor}}>{parse(props.fields.descriptionFieldValue, parserOptions)}</Description>
                {props.fields.showLink && (
                    <Link href={url}>
                        {props.fields.linkName}
                    </Link>
                )}
            </ContentBlock>
        </Wrapper>
    )}

    if(props.fields.linktype === "hazardous" && props.product.detail?.isHazardousGood) {
         return (
        <Wrapper className="hazardous-product">
            <ContentBlock>
                {props.fields.image && (
                    <StyledWrapper className="msg-image-wrapper" {...stylesCustom.imageWrapper} onMouseEnter={handleMouseEnterTooltip} onMouseLeave={handleMouseLeaveTooltip}>
                        <Img src={props.fields.image} altText={props.fields.imageAltText || ''} />
                        {props.fields.showIconTooltip &&
                            <>
                                {isVisibleTooltip && (
                                    <StyledWrapper className="tooltip-wrapper" {...stylesCustom.tooltipWrapper}>
                                        {props.fields.iconTooltipText}
                                        <Typography className="close-button" onClick={handleCloseClickTooltip}>
                                            <X/>
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </>
                        }
                    </StyledWrapper>
                )}
                <Description style={{color: props.fields.messageColor}}>{parse(props.fields.descriptionFieldValue, parserOptions)}</Description>
                {props.fields.showLink && (
                    <Link onClick={scrollToTarget}>
                        {props.fields.linkName}
                    </Link>
                )}
            </ContentBlock>
        </Wrapper>
    )}

    if(props.fields.linktype === "badgeText" && props.product.badges && props.product.badges.filter(x=>x.name.toLowerCase() === props.fields.badgeName.toLowerCase()).length > 0) {
        return (
            <Wrapper className="clearance-product">
                <ContentBlock>
                    {props.fields.image && (
                        <StyledWrapper className="msg-image-wrapper" {...stylesCustom.imageWrapper} onMouseEnter={handleMouseEnterTooltip} onMouseLeave={handleMouseLeaveTooltip}>
                            <Img src={props.fields.image} altText={props.fields.imageAltText || ''} />
                            {props.fields.showIconTooltip && (
                                <>
                                    {isVisibleTooltip && (
                                        <StyledWrapper className="tooltip-wrapper" {...stylesCustom.tooltipWrapper}>
                                            {props.fields.iconTooltipText}
                                            <Typography className="close-button" onClick={handleCloseClickTooltip}>
                                                <X/>
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    )}
                    <Description style={{color: props.fields.messageColor}}>{parse(props.fields.descriptionFieldValue, parserOptions)}</Description>
                    {props.fields.showLink && (
                        <Link href={url}>
                            {props.fields.linkName}
                        </Link>
                    )}
                </ContentBlock>
            </Wrapper>
    )}
    return null;
};



const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.linktype,
            displayName:"Content Type",
            editorTemplate: "RadioButtonsField",
            fieldType: "General",
            defaultValue: "discontinued",
            isRequired: true,
            sortOrder: 0,
            options: [
                {
                    displayName: "Discontinued",
                    value: "discontinued",
                },
                {
                    displayName: "Hazardous",
                    value: "hazardous",
                },
                {
                    displayName: "Badge Text",
                    value: "badgeText",
                },
            ],
        },
       
        {
            fieldType: "General",
            displayName: "Badge Name",
            name: fields.badgeName,
            editorTemplate: "TextField",
            defaultValue: "",
            isVisible: widget => widget.fields.linktype === 'badgeText',
            sortOrder: 1,
            isRequired:true,
        },
        {
            name: fields.descriptionFieldValue,
            displayName: "Description",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Contextual",
            isRequired: true,
            sortOrder: 2,
            extendedConfig: { height: 100 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
        },
        {
            name: fields.messageColor,
            editorTemplate: "ColorPickerField",
            displayName: "Message Color",
            defaultValue: "#1B1C1C",
            fieldType: "General",
        },
        {
            fieldType: "Contextual",
            name: fields.image,
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            sortOrder: 3,
        },
        {
            fieldType: "General",
            name: fields.imageAltText,
            editorTemplate: "TextField",
            defaultValue: "",
            sortOrder: 3,
        },
        {
            name: fields.showLink,
            displayName: "Show Button",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            sortOrder: 4,
        },
        {
            name: fields.learnMoreLink,
            displayName: "Button Link",
            fieldType: "Contextual",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            isVisible: widget => widget.fields.showLink,
            sortOrder: 5,
        },
        {
            name: fields.linkName,
            displayName: "Button Label",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Enter Button Name",
            isVisible: widget => widget.fields.showLink,
            sortOrder: 6,
        },
        {
            name: fields.showIconTooltip,
            displayName: "Show Icon Tooltip",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
            sortOrder: 7,
        },
        {
            fieldType: "General",
            displayName: "Icon Tooltip Text",
            name: fields.iconTooltipText,
            editorTemplate: "TextField",
            defaultValue: "",
            isVisible: widget => widget.fields.showIconTooltip,
            sortOrder: 8,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withProduct(DsgCommonMessageWidget),
    definition,
};

export default widgetModule;
